import React, { createContext, useContext, useState, useCallback } from 'react';
import { useAuth } from './AuthContext';
import * as documentService from '../services/documentService';
import { parseError } from '../utils/errorMessages';

const DocumentContext = createContext(null);

export const useDocument = () => {
  const context = useContext(DocumentContext);
  if (!context) throw new Error('useDocument must be used within DocumentProvider');
  return context;
};

export const DocumentProvider = ({ children }) => {
  const { authenticatedFetch } = useAuth();
  const [documents, setDocuments] = useState([]);
  const [currentDocument, setCurrentDocument] = useState(null);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState(null);

  const fetchClientDocuments = useCallback(async (clientId) => {
    setLoading(true);
    setError(null);
    try {
      const data = await documentService.getClientDocuments(authenticatedFetch, clientId);
      setDocuments(data);
      return data;
    } catch (err) {
      setError(parseError(err).message);
      return [];
    } finally {
      setLoading(false);
    }
  }, [authenticatedFetch]);

  const uploadNewDocument = useCallback(async (clientId, file, documentType, holdingId, notes) => {
    setUploading(true);
    setError(null);
    try {
      const data = await documentService.uploadDocument(authenticatedFetch, clientId, file, documentType, holdingId, notes);
      setDocuments(prev => [data, ...prev]);
      return data;
    } catch (err) {
      setError(parseError(err).message);
      throw err;
    } finally {
      setUploading(false);
    }
  }, [authenticatedFetch]);

  const fetchDocumentDetail = useCallback(async (documentId) => {
    setLoading(true);
    setError(null);
    try {
      const data = await documentService.getDocumentDetail(authenticatedFetch, documentId);
      setCurrentDocument(data);
      return data;
    } catch (err) {
      setError(parseError(err).message);
      return null;
    } finally {
      setLoading(false);
    }
  }, [authenticatedFetch]);

  const confirmDocumentExtraction = useCallback(async (documentId, extractedData) => {
    setError(null);
    try {
      const data = await documentService.confirmExtraction(authenticatedFetch, documentId, extractedData);
      setDocuments(prev => prev.map(d => d.id === documentId ? { ...d, ...data } : d));
      setCurrentDocument(data);
      return data;
    } catch (err) {
      setError(parseError(err).message);
      throw err;
    }
  }, [authenticatedFetch]);

  const downloadDocument = useCallback(async (documentId) => {
    try {
      const data = await documentService.getDownloadUrl(authenticatedFetch, documentId);
      window.open(data.url, '_blank');
    } catch (err) {
      setError(parseError(err).message);
    }
  }, [authenticatedFetch]);

  const getDocumentPreviewUrl = useCallback(async (documentId) => {
    try {
      const data = await documentService.getDownloadUrl(authenticatedFetch, documentId);
      return data.url;
    } catch (err) {
      setError(parseError(err).message);
      return null;
    }
  }, [authenticatedFetch]);

  const applyExtractionToHolding = useCallback(async (documentId, holdingId, fieldsToApply) => {
    setError(null);
    try {
      const data = await documentService.applyExtraction(authenticatedFetch, documentId, holdingId, fieldsToApply);
      return data;
    } catch (err) {
      setError(parseError(err).message);
      throw err;
    }
  }, [authenticatedFetch]);

  const deleteExistingDocument = useCallback(async (documentId) => {
    setError(null);
    try {
      await documentService.deleteDocument(authenticatedFetch, documentId);
      setDocuments(prev => prev.filter(d => d.id !== documentId));
      if (currentDocument?.id === documentId) {
        setCurrentDocument(null);
      }
    } catch (err) {
      setError(parseError(err).message);
      throw err;
    }
  }, [authenticatedFetch, currentDocument?.id]);

  const value = {
    documents,
    currentDocument,
    loading,
    uploading,
    error,
    setError,
    fetchClientDocuments,
    uploadNewDocument,
    fetchDocumentDetail,
    confirmDocumentExtraction,
    applyExtractionToHolding,
    downloadDocument,
    getDocumentPreviewUrl,
    deleteExistingDocument,
  };

  return (
    <DocumentContext.Provider value={value}>
      {children}
    </DocumentContext.Provider>
  );
};
